/**
 * Language switch inside the user menu (DEV-150).
 *
 * Built like the design switch next to it: a radiogroup, so arrow keys work and
 * a screen reader announces the selected entry. Each language is named in its
 * own language and therefore NOT translated — a user who landed in the wrong
 * language must still recognise their own.
 */

import { useLingui } from '@lingui/react/macro';
import { useState, type ReactNode } from 'react';

import { changeLocale, currentLocale, type AppLocale } from '../lib/locale';

const OPTIONS: readonly { value: AppLocale; label: string }[] = [
  { value: 'de', label: 'Deutsch' },
  { value: 'en', label: 'English' },
];

export function LanguageControl(): ReactNode {
  const { t } = useLingui();
  const [switching, setSwitching] = useState(false);
  const active = currentLocale();

  async function select(locale: AppLocale): Promise<void> {
    if (locale === active || switching) return;
    setSwitching(true);
    try {
      await changeLocale(locale);
    } finally {
      setSwitching(false);
    }
  }

  return (
    <div className="theme-segmented language-segmented" role="radiogroup" aria-label={t`Sprache`}>
      {OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          lang={option.value}
          aria-checked={active === option.value}
          className={active === option.value ? 'active' : undefined}
          disabled={switching}
          onClick={() => void select(option.value)}
        >
          <span>{option.label}</span>
        </button>
      ))}
    </div>
  );
}

export default LanguageControl;
